import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PlusCircle, Trash2, ChefHat, Info, Save, TrendingUp, TrendingDown, Link as LinkIcon, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { PendingImports } from '../components/PendingImports';

export const Recipes = () => {
  const { selectedClient, API } = useClient();
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const [itemsRes, ingRes] = await Promise.all([
        axios.get(`${API}/menu/items/${selectedClient.id}`),
        axios.get(`${API}/ingredients/${selectedClient.id}`)
      ]);
      setItems(itemsRes.data.items);
      setIngredients(ingRes.data.ingredients);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedClient]);

  if (!selectedClient) return <div className="text-center text-text-secondary py-12">Select a client</div>;

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <div>
        <h2 className="text-3xl font-bold text-primary tracking-tight">Recipe Linking</h2>
        <p className="text-text-secondary mt-1">Connect menu items to ingredients · auto-recalculates cost when supplier prices change</p>
      </div>

      {/* How it works notice */}
      <div className="bg-blue-50 border-l-4 border-state-info rounded-md p-4">
        <div className="flex items-start space-x-3">
          <LinkIcon className="h-5 w-5 text-state-info flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-state-info mb-1">How recipe linking works</p>
            <ol className="text-text-secondary space-y-1 text-xs list-decimal list-inside">
              <li>Define ingredients with current price (auto-created from supplier invoices)</li>
              <li>Link each menu item to its ingredients with quantities (e.g., Butter Chicken = 0.2 kg chicken + 0.05 kg butter)</li>
              <li>When you log a supplier invoice, the system auto-updates the ingredient's "current_price"</li>
              <li>All menu items using that ingredient recompute their "recipe_cost" in real-time</li>
              <li>Compare <strong>recipe_cost (live, derived)</strong> vs <strong>cost_price (manual)</strong> to spot food cost erosion</li>
            </ol>
          </div>
        </div>
      </div>

      <Tabs defaultValue="recipes" className="space-y-6">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="recipes" data-testid="tab-recipes">Menu Recipes</TabsTrigger>
          <TabsTrigger value="ingredients" data-testid="tab-ingredients">Ingredients Master</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-recipe-upload">
            <Sparkles className="h-3 w-3 mr-1 text-state-info" /> AI Upload
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          <ModuleUploadZone
            module="recipes"
            moduleLabel="Recipe Sheet"
            clientId={selectedClient.id}
            API={API}
            onUploadComplete={fetchData}
          />
          <PendingImports clientId={selectedClient.id} API={API} module="recipes" onChange={fetchData} />
        </TabsContent>

        <TabsContent value="recipes">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items list */}
            <div className="bg-white border border-border rounded-md p-4 lg:col-span-1">
              <h3 className="font-semibold text-primary mb-3">Menu Items ({items.length})</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      selectedItem?.id === item.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/30'
                    }`}
                    data-testid={`select-item-${item.id}`}
                  >
                    <p className="font-medium text-sm text-text-primary">{item.name}</p>
                    <div className="flex justify-between text-xs text-text-muted mt-1">
                      <span>{item.category}</span>
                      <div className="flex space-x-2">
                        <span>Cost: ₹{item.cost_price}</span>
                        {item.recipe_cost !== undefined && item.recipe_cost !== null && (
                          <span className={item.recipe_cost > item.cost_price ? 'text-state-error font-semibold' : 'text-state-success font-semibold'}>
                            Recipe: ₹{item.recipe_cost}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Recipe editor */}
            <div className="lg:col-span-2">
              {selectedItem ? (
                <RecipeEditor item={selectedItem} ingredients={ingredients} clientId={selectedClient.id} onSaved={fetchData} />
              ) : (
                <div className="bg-white border border-border rounded-md p-12 text-center text-text-muted">
                  <ChefHat className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Select a menu item to view or edit its recipe</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ingredients">
          <IngredientsManager ingredients={ingredients} clientId={selectedClient.id} onRefresh={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const RecipeEditor = ({ item, ingredients, clientId, onSaved }) => {
  const { API } = useClient();
  const [recipe, setRecipe] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadRecipe = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/recipes/${clientId}/${item.id}`);
        if (res.data.recipe) {
          setRecipe(res.data.recipe);
          setRows(res.data.recipe.ingredients.map(i => ({
            ingredient_id: i.ingredient_id,
            quantity: i.quantity,
            name: i.name,
            unit: i.unit,
            current_price: i.current_price,
            line_cost: i.line_cost
          })));
        } else {
          setRecipe(null);
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadRecipe();
  }, [item.id]);

  const addRow = () => {
    setRows([...rows, { ingredient_id: '', quantity: 0 }]);
  };

  const updateRow = (idx, field, value) => {
    const newRows = [...rows];
    newRows[idx][field] = value;
    if (field === 'ingredient_id') {
      const ing = ingredients.find(i => i.id === value);
      if (ing) {
        newRows[idx].name = ing.name;
        newRows[idx].unit = ing.unit;
        newRows[idx].current_price = ing.current_price;
      }
    }
    if (newRows[idx].current_price && newRows[idx].quantity) {
      newRows[idx].line_cost = +(newRows[idx].current_price * newRows[idx].quantity).toFixed(2);
    }
    setRows(newRows);
  };

  const removeRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
  };

  const save = async () => {
    const validRows = rows.filter(r => r.ingredient_id && r.quantity > 0);
    if (validRows.length === 0) {
      alert('Add at least one ingredient with quantity > 0');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/recipes`, {
        client_id: clientId,
        menu_item_id: item.id,
        ingredients: validRows.map(r => ({ ingredient_id: r.ingredient_id, quantity: parseFloat(r.quantity) }))
      });
      onSaved();
      alert('Recipe saved. Menu item cost will auto-update.');
    } catch (err) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalCost = rows.reduce((s, r) => s + (r.line_cost || 0), 0);
  const variance = totalCost - item.cost_price;

  if (loading) return <div className="bg-white border border-border rounded-md p-8 text-center text-text-muted">Loading recipe...</div>;

  return (
    <div className="bg-white border border-border rounded-md p-6 space-y-4" data-testid="recipe-editor">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-primary">{item.name}</h3>
          <p className="text-xs text-text-muted">{item.category} • {item.sub_category}</p>
        </div>
        <div className="text-right text-sm">
          <p className="text-text-muted text-xs">Manual Cost: <span className="font-semibold text-text-primary">₹{item.cost_price}</span></p>
          <p className="text-text-muted text-xs">Selling: <span className="font-semibold text-state-success">₹{item.selling_price}</span></p>
        </div>
      </div>

      {ingredients.length === 0 ? (
        <div className="bg-yellow-50 border-l-4 border-state-warning p-3 rounded-md">
          <p className="text-sm text-state-warning">No ingredients yet. Add ingredients in the "Ingredients Master" tab first, or log supplier invoices to auto-create them.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Ingredient</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-right">Current Price</th>
                  <th className="px-3 py-2 text-right">Line Cost</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row, idx) => (
                  <tr key={idx} data-testid={`recipe-row-${idx}`}>
                    <td className="px-3 py-2">
                      <select
                        value={row.ingredient_id}
                        onChange={(e) => updateRow(idx, 'ingredient_id', e.target.value)}
                        className="w-full border border-border rounded px-2 py-1 text-sm"
                        data-testid={`recipe-ingredient-${idx}`}
                      >
                        <option value="">Select ingredient</option>
                        {ingredients.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" step="0.001"
                        value={row.quantity}
                        onChange={(e) => updateRow(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-20 border border-border rounded px-2 py-1 text-sm text-right"
                        data-testid={`recipe-qty-${idx}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-text-secondary">{row.unit || '-'}</td>
                    <td className="px-3 py-2 text-sm text-right">{row.current_price ? `₹${row.current_price}` : '-'}</td>
                    <td className="px-3 py-2 text-sm text-right font-semibold">{row.line_cost ? `₹${row.line_cost.toFixed(2)}` : '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => removeRow(idx)} className="text-state-error hover:bg-red-50 rounded p-1">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-primary/5">
                <tr>
                  <td colSpan="4" className="px-3 py-3 text-right font-semibold text-primary text-sm uppercase tracking-wider">Recipe Cost Total</td>
                  <td className="px-3 py-3 text-right font-bold text-primary">₹{totalCost.toFixed(2)}</td>
                  <td></td>
                </tr>
                {recipe && (
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-right text-xs text-text-muted">Variance vs Manual Cost (₹{item.cost_price})</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`text-sm font-semibold flex items-center justify-end ${variance > 0 ? 'text-state-error' : 'text-state-success'}`}>
                        {variance > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                        {variance > 0 ? '+' : ''}₹{variance.toFixed(2)}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>

          <div className="flex justify-between">
            <Button onClick={addRow} variant="outline" className="border-primary text-primary" data-testid="add-ingredient-row">
              <PlusCircle className="mr-2 h-4 w-4" /> Add Ingredient
            </Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="save-recipe">
              <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving...' : 'Save Recipe'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const IngredientsManager = ({ ingredients, clientId, onRefresh }) => {
  const { API } = useClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', unit: 'kg', current_price: '' });

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ingredients`, {
        client_id: clientId,
        name: form.name,
        unit: form.unit,
        current_price: parseFloat(form.current_price) || 0,
        source: 'manual'
      });
      setShowAdd(false);
      setForm({ name: '', unit: 'kg', current_price: '' });
      onRefresh();
    } catch (e) {
      alert('Failed to add ingredient');
    }
  };

  const SOURCE_COLORS = {
    manual: 'bg-gray-100 text-gray-700',
    supplier_invoice: 'bg-green-100 text-state-success',
    agmarknet: 'bg-blue-100 text-state-info',
    ai_estimated: 'bg-orange-100 text-accent'
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-text-secondary text-sm">{ingredients.length} ingredients in master list</p>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-hover" data-testid="add-ingredient-button">
              <PlusCircle className="mr-2 h-4 w-4" /> Add Ingredient
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Ingredient</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-3">
              <input type="text" placeholder="Name (e.g., Tomato)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="ingredient-name-input" />
              <select value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" data-testid="ingredient-unit-select">
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="ltr">ltr</option>
                <option value="ml">ml</option>
                <option value="pcs">pcs</option>
              </select>
              <input type="number" step="0.01" placeholder="Current price per unit (₹)" value={form.current_price} onChange={(e) => setForm({...form, current_price: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" data-testid="ingredient-price-input" />
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" data-testid="submit-ingredient-button">Add</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-primary text-white text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-center">Unit</th>
              <th className="px-4 py-3 text-right">Current Price</th>
              <th className="px-4 py-3 text-center">Source</th>
              <th className="px-4 py-3 text-left">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {ingredients.length === 0 ? (
              <tr><td colSpan="5" className="text-center text-text-muted py-8">No ingredients yet. Add manually or log supplier invoices to auto-create them.</td></tr>
            ) : ingredients.map((ing, idx) => (
              <tr key={ing.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'} data-testid={`ingredient-row-${ing.id}`}>
                <td className="px-4 py-3 text-sm font-medium">{ing.name}</td>
                <td className="px-4 py-3 text-sm text-center text-text-secondary">{ing.unit}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">₹{ing.current_price}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${SOURCE_COLORS[ing.price_source] || SOURCE_COLORS.manual}`}>
                    {ing.price_source?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-text-muted">{ing.last_updated ? new Date(ing.last_updated).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
